import { titleCase } from "../../../utils";
import {
  IdentificationProvider,
  TVerificationAppElement,
  VerificationAppDocumentType,
} from "../../app/models";
import {
  getQuestionnaireDocumentTypes,
  QuestionnaireStepMatch,
} from "./questionnaireDocumentMatching";
import {
  ADDABLE_AMLBOT_DOC_ONLY_IDENTIFICATION_DOCUMENT_TYPES,
  isAmlbotDocOnlyIdentificationStep,
} from "./identificationDocumentEdit";

export type AddableDocumentCandidate = {
  stepConfig: TVerificationAppElement;
  documentType: string;
  label: string;
};

export type QuestionnaireDocumentTypesResolver = (
  stepConfig: TVerificationAppElement,
) => string[];

export type AddableDocumentCandidateOptions = {
  identificationProvider?: IdentificationProvider;
  documentTypesResolver?: QuestionnaireDocumentTypesResolver;
};

function getStepLabel(stepConfig: TVerificationAppElement): string {
  return (
    stepConfig.title ||
    titleCase(stepConfig.name?.trim() ? stepConfig.name : stepConfig.type)
  );
}

function getCandidateLabel(
  stepConfig: TVerificationAppElement,
  documentType: string,
  producedDocumentTypeCount: number,
): string {
  const stepLabel = getStepLabel(stepConfig);

  if (producedDocumentTypeCount > 1) {
    return `${stepLabel} - ${titleCase(documentType)}`;
  }

  return stepLabel;
}

export function getAddableDocumentCandidates(
  stepDocuments: QuestionnaireStepMatch[],
  canEdit: boolean,
  options: AddableDocumentCandidateOptions = {},
): AddableDocumentCandidate[] {
  if (!canEdit) {
    return [];
  }

  const {
    identificationProvider,
    documentTypesResolver = getQuestionnaireDocumentTypes,
  } = options;
  const seenDocumentTypes = new Set<string>();
  const candidates: AddableDocumentCandidate[] = [];

  for (const { element, documents } of stepDocuments) {
    if (element.type === VerificationAppDocumentType.Identification) {
      if (
        !isAmlbotDocOnlyIdentificationStep(element, identificationProvider) ||
        documents.length > 0
      ) {
        continue;
      }

      const producedDocumentTypes = Array.from(
        ADDABLE_AMLBOT_DOC_ONLY_IDENTIFICATION_DOCUMENT_TYPES,
      );

      for (const documentType of producedDocumentTypes) {
        if (seenDocumentTypes.has(documentType)) {
          continue;
        }

        seenDocumentTypes.add(documentType);
        candidates.push({
          stepConfig: element,
          documentType,
          label: getCandidateLabel(
            element,
            documentType,
            producedDocumentTypes.length,
          ),
        });
      }
      continue;
    }

    const producedDocumentTypes = Array.from(
      new Set(documentTypesResolver(element)),
    );
    const existingDocumentTypes = new Set(
      documents.map((document) => document.document.documentType),
    );

    for (const documentType of producedDocumentTypes) {
      if (existingDocumentTypes.has(documentType)) {
        continue;
      }
      if (seenDocumentTypes.has(documentType)) {
        continue;
      }

      seenDocumentTypes.add(documentType);
      candidates.push({
        stepConfig: element,
        documentType,
        label: getCandidateLabel(
          element,
          documentType,
          producedDocumentTypes.length,
        ),
      });
    }
  }

  return candidates;
}
